package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.sdk.currentSession
import com.ilussobsa.sdk.isSeller
import com.ilussobsa.sdk.participatesInAuctions
import com.ilussobsa.utils.calendar
import com.ilussobsa.utils.iconButton
import com.ilussobsa.utils.notifications2
import com.ilussobsa.utils.upcomingAuctions
import com.lightningkite.atZone
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.locale.RenderSize
import com.lightningkite.kiteui.locale.renderToString
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.pageNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.AppState
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.RecyclerViewPlacerVerticalGrid
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlinx.coroutines.launch
import kotlinx.datetime.*

@Routable("/upcomingAuctions")
class UpcomingAuctionsScreen : Page {

    override fun ViewWriter.render() = col {
        val upcomingAuctions = remember {
            val session = currentSession()
            val localDateToday = now().toLocalDateTime(TimeZone.currentSystemDefault()).date
            val localEndDate = localDateToday + DatePeriod(days = 30)
            val startTimeLocal = localDateToday.atStartOfDayIn(TimeZone.currentSystemDefault())
            val endTimeLocal = localEndDate.atStartOfDayIn(TimeZone.currentSystemDefault())
            val startTimeServer = startTimeLocal.toLocalDateTime(auctionZone)
            val endTimeServer = endTimeLocal.toLocalDateTime(auctionZone)

            session.auctionLane.query(Query(orderBy = sort { it.scheduledStart }) {
                (it.scheduledStart lt endTimeServer) and (it.scheduledStart gte startTimeServer)
            })().sortedBy { it.scheduledStart }
        }
        val items = remember {
            upcomingAuctions().mapNotNull { auctionLane ->
                currentSession().vehicle.query(Query(orderBy = sort { it.orderingValue }, limit = 1) {
                    it.auctionLane.eq(auctionLane._id) and it.submitted.neq(null) and
                            it.archived.eq(null) and it.completion.eq(null)
                })().firstOrNull()
            }
        }
        card - expanding - col {
            row {
                centered - icon(Icon.upcomingAuctions, Strings.upcomingAuctions)
                centered - h2(Strings.upcomingAuctions)
            }
           expanding - frame {
                recyclerView {
                    ::placer {
                        var horizontalSpaceEstimate = AppState.windowInfo().width.px
                        if (horizontalSpaceEstimate >= 1000) horizontalSpaceEstimate -= 250
                        val numColumns = (horizontalSpaceEstimate / 400.dp.px).toInt().coerceAtLeast(1)
                        RecyclerViewPlacerVerticalGrid(numColumns)
                    }
                    children(
                        items = items,
                        id = { it._id }
                    ) {
                        frame {
                            sizeConstraints(height = 400.dp) - upcomingAuctionCard(it)
                        }
                    }
                }
            }
        }
    }
}

fun ViewWriter.upcomingAuctionCard(vehicle: Reactive<Vehicle>): ViewModifiable {
    val id = remember { vehicle()._id }
    val notify = remember {
        vehicleRelationshipUpsertProp(id(), VehicleRelationship.path.notifications)
    }.flatten()
    val auctionDate = remember {
        vehicle().auctionLane?.let { currentSession().auctionLane[it]() }?.scheduledStartInstant
    }
    return important - col {
        ::shown { vehicle().auctionLane !== null  }
        row {
            expanding - h3 {
                ::content{
                    vehicle().mmt
                }
            }

            align(Align.End, Align.Start) - row {
                ::shown {
                    !isSeller() && vehicle().liveAt == null
                }
                compact - iconButton - toggleButton {

                    icon {
                        ::source {
                            Icon.notifications2
                        }
                        description = Strings.notificationsEnabled
                    }
                    checked bind notify
                }
            }
        }
        compact - rowCollapsingToColumn(550.dp) {
            card - row {
                ::shown { auctionDate() != null }
                centered - icon {
                    ::source { Icon.calendar }
                    description = "Auction date"
                }
                bold - centered - text {
                    ::content label@{
                        val asZoned = auctionDate()?.atZone(auctionZone) ?: return@label ""
                        buildString {
                            append(asZoned.time.renderToString())
                            append(" ")
                            append("PST")
                            append(" - ")
                            append(asZoned.date.renderToString(RenderSize.Numerical))
                        }
                    }
                }
            }
            expanding - space()
            WarningSemantic.plus(ButtonSemantic).onNext - button {
                paddingByEdge = Edges(1.rem, 0.5.rem, 1.rem, 0.5.rem)
                centered - text("Preview")
                onClick { pageNavigator.navigate(VehicleDetailScreen(vehicle()._id)) }
            }
        }
        compact - rowCollapsingToColumn(550.dp) {
            compact - row {
                centered - WarningSemantic.onNext - text(Strings.year2)
                centered - text { ::content{ vehicle().year.toString() } }
            }
            space()
            compact - row {
                centered - WarningSemantic.onNext - text(Strings.mileage2)
                centered - text { ::content{ vehicle().odometer?.toCommaString() ?: Strings.noMileageInformation } }
            }
            expanding - space()
            compact - row {
                val proxyBid = remember {
                    vehicle()._id.let {
                        vehicleRelationshipUpsertProp(
                            it,
                            VehicleRelationship.path.autobid
                        )()
                    }
                }
                ::shown {
                    participatesInAuctions() && proxyBid() != null
                }

                centered - WarningSemantic.onNext - text(Strings.yourProxyBid)
                centered - text { ::content{ proxyBid()?.renderKPriceInDollars() ?: "" } }
            }
        }
        expanding - tweakTheme { it.copy(cornerRadii = CornerRadii.Constant(0.75.rem)) } - image {
            scaleType = ImageScaleType.Crop
            ::source { vehicle().thumbnail?.let { ImageRemote(it.location) } }
        }

    }
}
